import React, {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import rehypeRaw from 'rehype-raw';
import Markdown from 'react-markdown';

import style from './style.module.less';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { actions } from '../../redux/slices';
import selectors from '../../redux/selectors';
import { Header } from './header';
import { usePrevious } from '../../utils/use-previous';
import ChevronDownIcon from './chevron-down.svg?react';
import ChevronUpIcon from './chevron-up.svg?react';
import { AUTH_STORAGE_KEY } from '../../constants';
import { FileContainer } from './files';
import { CreateMatter } from './create-matter';
import noPrematterImage from './no-prematter.png';
import { Typography } from '@galaxy-ds/core';
import DocumentIcon from './document.svg?react';
import { MatterCreatedToast } from '../../components/matter-created-toast';

const MAX_HEIGHT = 200;

export const InformationContainer: FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  // we want to set this to true if the info description is longer than required,
  // we then want to show the see more button
  const [shouldShowMore, setShouldShowMore] = useState(false);
  const [seeMore, setSeeMore] = useState(false);
  const descriptionsRef = useRef<HTMLDivElement | null>(null);

  const connection = useAppSelector(
    selectors.lawyerConnection.getConnectionById(id)
  );
  const prevId = usePrevious(id);
  const prematter = useAppSelector(
    selectors.prematter.getPrematterById(connection?.prematterId)
  );
  const isFetchingPrematter = useAppSelector(
    selectors.prematter.isFetchingByConnectionId()
  );
  const isConvertingToMatter = useAppSelector(
    selectors.prematter.isConvertingToMatter()
  );
  const [isCreateMatterModalOpen, setIsCreateMatterModalOpen] = useState(false);
  const closeCreateMatterModal = () => {
    setIsCreateMatterModalOpen(false);
  };
  const openMatterCreatedToast = () => {
    setIsCreateMatterModalOpen(true);
  };


  useEffect(() => {
    dispatch(actions.lawyerConnection.fetchById({ id }));
  }, [dispatch, id]);

  useEffect(() => {
    if (prevId !== id) {
      dispatch(actions.prematter.fetchByConnectionId({ connectionId: id }));
    }
  }, [id, prevId, dispatch]);

  const downloadMatter = () => {
    // open the link in new tab
    if (connection?.prematterId) {
      const token = window.sessionStorage.getItem(AUTH_STORAGE_KEY) || '';
      try {
        window.location.assign(
          // eslint-disable-next-line @stylistic/js/max-len
          `/api/prematter/${connection.prematterId}/report/download?jwt=${token}`
        );
      } catch (e) {
        console.error('Error downloading file', e);
      }
    } else {
      console.error('No prematter id found');
    }
  };
  // on div
  useLayoutEffect(() => {
    if (descriptionsRef?.current) {
      if (descriptionsRef.current.getBoundingClientRect().height > MAX_HEIGHT) {
        setShouldShowMore(true);
      }
    }
  }, [prematter?.description]);

  if (!id || !connection || isFetchingPrematter) {
    return null;
  } else if (!prematter) {
    return (
      <div className={style.noPrematterContainer}>
        <img
          src={noPrematterImage}
          alt='No prematter'
          className={style.noPrematterImage}
        />
        <div className={style.noPrematterText}>
          <Typography variant='body1'>{t('lawyer.case-removed')}</Typography>
        </div>
      </div>
    );
  }

  return (
    <div className={style.container}>
      <div className={style.body}>
        <Header
          Icon={DocumentIcon}
          title={prematter.name}
          // items={[
          //   {
          //     label: 'Create matter',
          //     onClick: convertToMatter,
          //     icon: <PlusIcon className={style.contextIcon} />,
          //   },
          //   {
          //     label: 'Download',
          //     onClick: downloadMatter,
          //     icon: <DownloadIcon className={style.contextIcon} />,
          //   },
          // ]}
        />
        <div
          // eslint-disable-next-line @stylistic/js/max-len
          className={`${style.descriptionContainer} ${shouldShowMore ? (seeMore ? style.showMore : style.showLess) : ''}`}
        >
          <div
            ref={descriptionsRef}
            className={style.descriptions}
            style={{ '--info-max-height': MAX_HEIGHT } as CSSProperties}
          >
            <Markdown
              rehypePlugins={[rehypeRaw]}
              className={style.descriptionMarkdown}
            >
              {prematter.description}
            </Markdown>
          </div>
          {shouldShowMore ? (
            !seeMore ? (
              <div className={style.seeMore} onClick={() => setSeeMore(true)}>
                <ChevronDownIcon className={style.arrowDown} />
                {t('lawyer.read-more')}
              </div>
            ) : (
              <div className={style.seeLess} onClick={() => setSeeMore(false)}>
                <ChevronUpIcon className={style.arrowDown} />
                {t('lawyer.hide')}
              </div>
            )
          ) : null}
        </div>
        <CreateMatter id={id} openMatterCreatedToast={openMatterCreatedToast} />
        <div className={style.filesContainer}>
          <FileContainer connection={connection} prematter={prematter} />
        </div>
      </div>
      <MatterCreatedToast
        isOpen={isCreateMatterModalOpen}
        onClose={closeCreateMatterModal}
      />
      {/* <TestingInputComponent /> */}
    </div>
  );
};
